import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, Printer, Plus, Trash2 } from 'lucide-react';
import {
  getProduzione, updateBigBag, addBigBag, deleteBigBag, assegnaTipologiaBigBag,
  getEtichettaBigBag, getEtichetteProduzione,
  type ProduzioneDetail, type BigBag,
} from '../../api/magazzino';
import StampaEtichettaModal from './StampaEtichettaModal';
import { getTipologieRifiutoProdotto } from '../../api/settings';
import { fmtKg } from '../../lib/saldo';

interface StampaTarget { title: string; filename: string; fetch: () => Promise<Blob>; }

interface Props {
  produzioneId: string;
  canEdit: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1 focus:outline-none focus:border-sage bg-white';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function ProduzioneDettaglioModal({ produzioneId, canEdit, onClose, onChanged }: Props) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<ProduzioneDetail | null>(null);
  const [bagEdits, setBagEdits] = useState<Record<string, { peso: string; data: string }>>({});
  const [newPeso, setNewPeso] = useState('');
  const [newData, setNewData] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stampa, setStampa] = useState<StampaTarget | null>(null);
  // Tipologie ATTIVE del CER della produzione: servono sia per assegnarne una a
  // un sacco che non ce l'ha (i big bag anteriori alla V74), sia per il sacco
  // nuovo, che una tipologia la esige come in registrazione.
  const [tipologie, setTipologie] = useState<{ id: string; nome: string }[]>([]);
  const [newTipologia, setNewTipologia] = useState('');

  const reload = useCallback(() => {
    void (async () => {
      const d = await getProduzione(produzioneId);
      setDetail(d);
      setBagEdits(Object.fromEntries(
        d.big_bags.map((b) => [b.id, { peso: String(b.peso_kg), data: b.data_produzione ?? '' }]),
      ));
    })();
  }, [produzioneId]);
  useEffect(() => { reload(); }, [reload]);

  // Il CER della produzione arriva col dettaglio. Il filtro per CODICE lo fa
  // l'endpoint (`?cer=`), che e' dove vive la stessa regola della validazione:
  // qui si chiedeva tutto il catalogo e si filtrava a mano, cioe' una terza
  // copia della regola (rilievo 4, PR #48).
  const materiale = detail?.materiale;
  useEffect(() => {
    if (!materiale) return;
    void (async () => {
      setTipologie(await getTipologieRifiutoProdotto(true, { cer: materiale }));
    })();
  }, [materiale]);

  function notify() { reload(); onChanged?.(); }

  async function cambiaTipologia(b: BigBag, tipologiaId: string) {
    if (!tipologiaId) return;
    setBusy(true); setError(null);
    try {
      await assegnaTipologiaBigBag(b.id, tipologiaId);
      notify();
    } catch (err: unknown) {
      // 409 del sacco uscito: il detail e' un oggetto, e il messaggio ci sta dentro.
      const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = (d && typeof d === 'object' && 'message' in d)
        ? String((d as { message: unknown }).message)
        : (typeof d === 'string' ? d : t('magazzino.produzione_dettaglio.errore_tipologia'));
      setError(msg);
    } finally { setBusy(false); }
  }

  async function salvaBigBag(b: BigBag) {
    const edit = bagEdits[b.id];
    if (!edit) return;
    setBusy(true); setError(null);
    try {
      await updateBigBag(b.id, { peso_kg: Number(edit.peso), data_produzione: edit.data || null });
      notify();
    } finally { setBusy(false); }
  }

  async function aggiungi() {
    const n = Number(newPeso);
    if (!Number.isFinite(n) || n <= 0) { setError(t('magazzino.produzione_dettaglio.peso_non_valido')); return; }
    setBusy(true); setError(null);
    try {
      if (!newTipologia) { setError(t('magazzino.produzione_dettaglio.tipologia_obbligatoria')); return; }
      await addBigBag(produzioneId, {
        peso_kg: n, data_produzione: newData || null, tipologia_id: newTipologia });
      setNewPeso(''); setNewData(today()); setNewTipologia('');
      notify();
    } finally { setBusy(false); }
  }

  async function rimuovi(b: BigBag) {
    setBusy(true); setError(null);
    try {
      await deleteBigBag(b.id);
      notify();
    } catch (err: unknown) {
      const detailMsg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detailMsg ?? t('magazzino.produzione_dettaglio.errore_rimozione'));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-ink-dark">{t('magazzino.produzione_dettaglio.titolo')}</h2>
          <div className="flex items-center gap-2">
            <button type="button"
              onClick={() => setStampa({
                title: t('magazzino.produzione_dettaglio.etichette_produzione'),
                filename: `etichette-produzione-${produzioneId}.pdf`,
                fetch: () => getEtichetteProduzione(produzioneId),
              })}
              className="px-2.5 py-1.5 text-xs text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg flex items-center gap-1">
              <Printer size={13} /> {t('magazzino.produzione_dettaglio.stampa_etichette')}
            </button>
            <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
          </div>
        </div>

        {!detail ? (
          <div className="p-8 text-center text-sm text-ink-soft">{t('magazzino.produzione_dettaglio.caricamento')}</div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Testata (peso = somma big bag, read-only) */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-ink-soft">{t('magazzino.produzione_dettaglio.lbl_area')}</span> <span className="font-medium">{detail.area_codice}</span></div>
              <div><span className="text-ink-soft">{t('magazzino.produzione_dettaglio.lbl_cer')}</span> <span className="font-medium">{detail.materiale}</span></div>
              <div><span className="text-ink-soft">{t('magazzino.produzione_dettaglio.lbl_operatore')}</span> <span className="font-medium">{detail.operatore_nome ?? '—'}</span></div>
              <div><span className="text-ink-soft">{t('magazzino.produzione_dettaglio.lbl_data')}</span> <span className="font-medium">{new Date(detail.created_at).toLocaleString('it-IT')}</span></div>
            </div>
            <div className="rounded-lg border border-stone-mid bg-sage/5 px-4 py-3">
              <span className="text-xs text-ink-soft">{t('magazzino.produzione_dettaglio.peso_totale')}</span>
              <p className="text-lg font-semibold text-ink-dark">{fmtKg(detail.peso_kg)} kg</p>
            </div>

            {error && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{error}</div>
            )}

            {/* Big bags */}
            <div>
              <p className="text-sm font-medium text-ink-soft mb-2">{t('magazzino.produzione_dettaglio.bag_count', { n: detail.big_bags.length })}</p>
              <div className="space-y-2">
                {detail.big_bags.map((b) => {
                  const edit = bagEdits[b.id] ?? { peso: String(b.peso_kg), data: b.data_produzione ?? '' };
                  const uscito = !!b.uscita_codice;
                  return (
                    <div key={b.id} className="flex flex-wrap items-end gap-2 border border-stone-mid rounded-lg p-2">
                      <div>
                        <label className="block text-xs text-ink-soft mb-0.5">{t('magazzino.produzione_dettaglio.peso_kg')}</label>
                        <input type="number" step="0.01" className={`${inputCls} w-24`} value={edit.peso} disabled={!canEdit || uscito}
                          onChange={(e) => setBagEdits((s) => ({ ...s, [b.id]: { ...edit, peso: e.target.value } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-ink-soft mb-0.5">{t('magazzino.produzione_dettaglio.data')}</label>
                        <input type="date" className={inputCls} value={edit.data} disabled={!canEdit || uscito}
                          onChange={(e) => setBagEdits((s) => ({ ...s, [b.id]: { ...edit, data: e.target.value } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-ink-soft mb-0.5">{t('magazzino.produzione_dettaglio.tipologia')}</label>
                        {/* Modificabile finche' il sacco e' in area: l'etichetta
                            non e' su nessun formulario. Uscito -> disabilitato,
                            e il 409 del backend resta l'autorita'. */}
                        <select className={`${inputCls} w-40`} disabled={!canEdit || uscito || busy}
                          data-testid={`bag-tipologia-${b.id}`}
                          value={b.tipologia_id ?? ''}
                          onChange={(e) => cambiaTipologia(b, e.target.value)}>
                          <option value="">{t('magazzino.produzione_dettaglio.tipologia_assente')}</option>
                          {tipologie.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
                        </select>
                      </div>
                      {canEdit && (
                        <button type="button" onClick={() => salvaBigBag(b)} disabled={busy || uscito}
                          data-testid={`bag-salva-${b.id}`}
                          className="px-2.5 py-1.5 text-xs text-white bg-sage rounded-lg hover:bg-sage-dark flex items-center gap-1 disabled:opacity-60">
                          <Save size={13} /> {t('magazzino.produzione_dettaglio.salva')}
                        </button>
                      )}
                      <button type="button"
                        onClick={() => setStampa({
                          title: t('magazzino.produzione_dettaglio.etichetta_bag'),
                          filename: `etichetta-${b.id}.pdf`,
                          fetch: () => getEtichettaBigBag(b.id),
                        })}
                        className="px-2.5 py-1.5 text-xs text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg flex items-center gap-1">
                        <Printer size={13} /> {t('magazzino.produzione_dettaglio.etichetta')}
                      </button>
                      {canEdit && detail.big_bags.length > 1 && (
                        <button type="button" onClick={() => rimuovi(b)} disabled={busy || uscito}
                          data-testid={`bag-rimuovi-${b.id}`}
                          title={uscito ? t('uscite.badge_uscito_titolo') : undefined}
                          className="px-2.5 py-1.5 text-xs text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg flex items-center gap-1 disabled:opacity-60">
                          <Trash2 size={13} /> {t('magazzino.produzione_dettaglio.rimuovi')}
                        </button>
                      )}
                      {uscito && (
                        <span data-testid={`bag-uscito-${b.id}`}
                          title={t('uscite.badge_uscito_titolo')}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-accent-warning-bg text-ink-mid whitespace-nowrap self-center">
                          {t('uscite.badge_uscito', { codice: b.uscita_codice })}
                        </span>
                      )}
                      <span className="text-[10px] text-ink-faint break-all ml-auto self-center max-w-[35%]">{b.qr_token}</span>
                    </div>
                  );
                })}
              </div>

              {/* Aggiungi big bag */}
              {canEdit && (
                <div className="flex flex-wrap items-end gap-2 mt-3 pt-3 border-t border-stone-mid">
                  <div>
                    <label className="block text-xs text-ink-soft mb-0.5">{t('magazzino.produzione_dettaglio.nuovo_bag_peso')}</label>
                    <input type="number" step="0.01" className={`${inputCls} w-28`} value={newPeso} onChange={(e) => setNewPeso(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-soft mb-0.5">{t('magazzino.produzione_dettaglio.data')}</label>
                    <input type="date" className={inputCls} value={newData} onChange={(e) => setNewData(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-soft mb-0.5">{t('magazzino.produzione_dettaglio.tipologia')}</label>
                    <select className={`${inputCls} w-40`} data-testid="bag-nuovo-tipologia"
                      value={newTipologia} onChange={(e) => setNewTipologia(e.target.value)}>
                      <option value="">{t('magazzino.produzione_dettaglio.tipologia_scegli')}</option>
                      {tipologie.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={aggiungi} disabled={busy || !newTipologia}
                    data-testid="bag-nuovo-aggiungi"
                    className="px-2.5 py-1.5 text-xs text-white bg-sage rounded-lg hover:bg-sage-dark flex items-center gap-1 disabled:opacity-60">
                    <Plus size={13} /> {t('magazzino.produzione_dettaglio.aggiungi')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {stampa && (
        <StampaEtichettaModal
          title={stampa.title}
          filename={stampa.filename}
          fetchPdf={stampa.fetch}
          onClose={() => setStampa(null)}
        />
      )}
    </div>
  );
}
